// Keep aligned with /shared/rentalHotelTimes.js (Upsun source.root is site-next only).
export function parseHotelIdFromRef(ref) {
  const m = String(ref || '').trim().match(/^hotel:(\d+)$/i);
  return m ? Number(m[1]) : null;
}

export function normalizeRentalHm(value) {
  const t = String(value || '').trim();
  if (!/^\d{2}:\d{2}/.test(t)) return '';
  return t.slice(0, 5);
}

export function normalizeHotelTimeList(list) {
  const out = [];
  const seen = new Set();
  (Array.isArray(list) ? list : []).forEach((item) => {
    const hm = normalizeRentalHm(item);
    if (!hm || seen.has(hm)) return;
    seen.add(hm);
    out.push(hm);
  });
  return out;
}

export function hotelRentalGuide(hotel) {
  const deliveryTimes = normalizeHotelTimeList(
    hotel?.delivery_times || hotel?.car_rental_delivery_times,
  );
  const collectionTimes = normalizeHotelTimeList(
    hotel?.collection_times || hotel?.car_rental_collection_times,
  );
  const legacyDropoff = normalizeRentalHm(
    hotel?.default_dropoff_time || hotel?.car_rental_dropoff_time,
  );
  return {
    deliveryTimes,
    collectionTimes: collectionTimes.length ? collectionTimes : (legacyDropoff ? [legacyDropoff] : []),
    deliveryNotes: String(hotel?.delivery_notes || hotel?.car_rental_delivery_notes || '').trim(),
    collectionNotes: String(
      hotel?.collection_notes
      || hotel?.car_rental_collection_notes
      || hotel?.dropoff_notes
      || hotel?.car_rental_dropoff_notes
      || '',
    ).trim(),
  };
}

export function lookupHotelRentalGuide(hotelsById, refOrId) {
  if (!hotelsById) return hotelRentalGuide(null);
  const id = typeof refOrId === 'number' ? refOrId : parseHotelIdFromRef(refOrId);
  if (!id) return hotelRentalGuide(null);
  const hotel = typeof hotelsById.get === 'function' ? hotelsById.get(id) : hotelsById[id];
  return hotelRentalGuide(hotel);
}

export function defaultHotelTime(times) {
  const list = normalizeHotelTimeList(times);
  return list[0] || '';
}

function addDaysYmd(ymd, daysToAdd) {
  const raw = String(ymd || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return '';
  const [y, m, d] = raw.split('-').map(Number);
  const utc = Date.UTC(y, m - 1, d);
  const next = new Date(utc + Math.max(0, Number(daysToAdd) || 0) * 86400000);
  return [
    next.getUTCFullYear(),
    String(next.getUTCMonth() + 1).padStart(2, '0'),
    String(next.getUTCDate()).padStart(2, '0'),
  ].join('-');
}

function hmToMinutes(hhmm, fallback) {
  const [h, m] = String(hhmm || '').split(':').map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return fallback;
  return Math.min(23 * 60 + 59, Math.max(0, h * 60 + m));
}

export function suggestCollectionFromDays(deliveryDate, deliveryTime, rentalDays, collectionTimes = []) {
  if (!deliveryDate) return null;
  const days = Math.max(1, parseInt(rentalDays, 10) || 1);
  const pickupMins = hmToMinutes(deliveryTime, 9 * 60);
  const hotelTime = defaultHotelTime(collectionTimes);
  if (pickupMins <= 12 * 60) {
    return {
      collection_date: addDaysYmd(deliveryDate, days - 1),
      collection_time: hotelTime || '21:00',
    };
  }
  const hh = String(Math.floor(pickupMins / 60)).padStart(2, '0');
  const mm = String(pickupMins % 60).padStart(2, '0');
  return {
    collection_date: addDaysYmd(deliveryDate, days),
    collection_time: hotelTime || `${hh}:${mm}`,
  };
}

function calendarDaysBetweenYmd(fromYmd, toYmd) {
  const a = String(fromYmd || '').slice(0, 10);
  const b = String(toYmd || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(a) || !/^\d{4}-\d{2}-\d{2}$/.test(b)) return null;
  const [y1, m1, d1] = a.split('-').map(Number);
  const [y2, m2, d2] = b.split('-').map(Number);
  const t1 = Date.UTC(y1, m1 - 1, d1);
  const t2 = Date.UTC(y2, m2 - 1, d2);
  return Math.floor((t2 - t1) / 86400000);
}

/** Inverse of suggestCollectionFromDays for the collection date. */
export function rentalDaysFromCollectionRange(deliveryYmd, deliveryHm, collectionYmd) {
  const calendar = calendarDaysBetweenYmd(deliveryYmd, collectionYmd);
  if (calendar == null || calendar < 0) return 1;
  const pickupMins = hmToMinutes(deliveryHm, 9 * 60);
  if (pickupMins <= 12 * 60) return Math.max(1, calendar + 1);
  return Math.max(1, calendar);
}
